import { ReactNode, useEffect, useState } from "react";
import { CONTAINS_URL_REGEX } from "../../models/Validators";
import Modal from "../Modal/Modal";
import { Button } from "../Control/Button/Button";
import { AnchorButton } from "../Control/Button/AnchorButton";
import WarningAlert from "../Alerts/WarningAlert";
import { classNames } from "../../style/common";

export type TextDisplayProps = {
    value: string,
    classNames?: {
        container?: string | undefined
    }
}

export function TextDisplay(props: TextDisplayProps) {
    const [isUrlConfirmationOpen, setIsUrlConfirmationOpen] = useState<boolean>(false);
    const [url, setUrl] = useState("");
    const [displayUrl, setDisplayUrl] = useState("");
    
    const onUrlClick = (url: string, displayUrl: string) => {
        setUrl(url);
        setDisplayUrl(displayUrl);
        setIsUrlConfirmationOpen(true);
    }

    return (
        <>
            <div className={classNames("whitespace-pre-wrap", props.classNames?.container)}>
                {
                    getSubstringElements(props.value, onUrlClick).map((value, index) => <span key={index}>{value}</span>)
                }
            </div>

            <Modal isOpen={isUrlConfirmationOpen} onOpenChange={() => setIsUrlConfirmationOpen(false)} title="Open URL?" classNames={{ dialogPanel: "!w-[400px]" }}>
                <p className="my-4">Are you sure you want to navigate to <span className="font-bold">{displayUrl}</span>?</p>
                <WarningAlert text="Navigating to URLs provided by other users can potentially be dangerous. Before proceeded, be sure you trust the website being linked to." />
                <div className="space-x-2 mt-8">
                    <Button
                        onClick={() => {
                            setIsUrlConfirmationOpen(false);
                        }}
                        color="component">
                        Cancel
                    </Button>
                    <AnchorButton
                        href={url}
                        target="_blank"
                        onClick={() => {
                            setIsUrlConfirmationOpen(false);
                        }}
                        color="danger">
                        Yes
                    </AnchorButton>
                </div>
            </Modal>
        </>
    )
}

function getSubstringElements(value: string, onClick: (url: string, displayUrl: string) => void): ReactNode[] {
    // Contains all the URLs found
    const matchIterator = value.matchAll(CONTAINS_URL_REGEX);
    const matches: RegExpMatchArray[] = [];

    for (const match of matchIterator) {
        matches.push(match);
    }

    // Contains everything but the URLs found
    const parts = value.split(CONTAINS_URL_REGEX).filter(x => x !== undefined);
    const result: ReactNode[] = [];

    let matchIndex = 0;

    for (const part of parts) {
        result.push(part);

        if (matchIndex <= matches.length - 1) {
            const url = matches[matchIndex][0];
            result.push(
                <MockLink
                    onClick={onClick}
                    value={url} />
            );
            matchIndex = matchIndex + 1;
        }
    }

    return result;
}

type MockLinkProps = {
    value: string,
    onClick: (url: string, displayUrl: string) => void
}

function MockLink(props: MockLinkProps) {
    const [url, setUrl] = useState(props.value);

    const validUrls = [
        "materiahunter.com",
        "www.materiahunter.com",
        "https://materiahunter.com",
        "https://www.materiahunter.com"
    ];

    useEffect(() => {
        if (!props.value.startsWith("http://") && !props.value.startsWith("https://")) {
            setUrl(`//${props.value}`);
        } else {
            setUrl(props.value);
        }
    }, [props.value]);

    if (validUrls.some(x => props.value.startsWith(x))) {
        return (
            <a href={url} target="_blank" className="text-emphasis hover:text-emphasis-secondary cursor-pointer">{props.value}</a>    
        )
    }

    return (
        <span className="text-emphasis hover:text-emphasis-secondary cursor-pointer" onClick={() => props.onClick(url, props.value)}>{props.value}</span>
    )
}